/**
 * Marca de la fecha límite (CDT-76) en la tarjeta, la lista y la ficha.
 *
 * - **Atrasada** (`estaAtrasada` del contrato: fecha anterior a hoy y abierta): rojo de peligro, «Atrasada · 12 nov».
 * - **Vence pronto** (`vencePronto`: faltan 2 días o menos): ámbar, «Vence hoy», «Vence mañana» o «Vence 14 nov».
 * - Si no: neutra, «Vence 20 nov» (o solo «20 nov» en una tarea cerrada, que ya no vence).
 *
 * «Hoy» es el día en Chile (`hoyEnChile`), igual que el filtro `?atrasada=` de la API. Una sola línea con «…»; el
 * `title` lleva la fecha completa. Contraste del texto sobre su fondo en claro, oscuro y cada paleta: lo revisa
 * features/tema/contraste.test.ts.
 */
import {
  diasHastaFechaLimite,
  esEstadoCerrado,
  estaAtrasada,
  vencePronto,
  type Estado,
} from '@cdtec-backlog/contrato';
import { Icono } from '../../componentes/Icono';
import { diaCorto, diaLargo } from '../../lib/formato';
import './FechaLimite.css';

type Situacion = 'atrasada' | 'pronto' | 'normal' | 'cerrada';

interface TareaConFecha {
  fechaLimite: string | null;
  estado: Estado;
}

export function situacionFechaLimite(tarea: TareaConFecha, hoy: string): Situacion | null {
  if (tarea.fechaLimite === null) return null;
  if (esEstadoCerrado(tarea.estado)) return 'cerrada';
  if (estaAtrasada(tarea, hoy)) return 'atrasada';
  if (vencePronto(tarea, hoy)) return 'pronto';
  return 'normal';
}

/** Texto visible y el del `title` (más completo). */
export function textoFechaLimite(
  tarea: TareaConFecha,
  hoy: string,
): { texto: string; titulo: string } | null {
  const situacion = situacionFechaLimite(tarea, hoy);
  if (situacion === null || tarea.fechaLimite === null) return null;
  const corto = diaCorto(tarea.fechaLimite);
  const titulo = `Fecha límite: ${diaLargo(tarea.fechaLimite)}`;
  switch (situacion) {
    case 'atrasada':
      return { texto: `Atrasada · ${corto}`, titulo: `${titulo} (atrasada)` };
    case 'pronto': {
      const dias = diasHastaFechaLimite(tarea.fechaLimite, hoy);
      const texto = dias === 0 ? 'Vence hoy' : dias === 1 ? 'Vence mañana' : `Vence ${corto}`;
      return { texto, titulo };
    }
    case 'normal':
      return { texto: `Vence ${corto}`, titulo };
    case 'cerrada':
      return { texto: corto, titulo };
  }
}

export function MarcaFechaLimite({ tarea, hoy }: { tarea: TareaConFecha; hoy: string }) {
  const situacion = situacionFechaLimite(tarea, hoy);
  const textos = textoFechaLimite(tarea, hoy);
  if (situacion === null || textos === null) return null;
  return (
    <span className={`marca-limite marca-limite--${situacion}`} title={textos.titulo}>
      <Icono nombre="calendario" tamano={12} />
      <span>{textos.texto}</span>
    </span>
  );
}
